import { toExcelAmount, toExcelDate, toExcelQuantity } from 'components/xlsx/excelCells';
import { ClassifierData } from 'types/classifiers';
import { MatterExperienceRecordListExportItem } from 'types/experience-records';
import type { ExportMapperOptions } from 'types/exports';
import { MatterEmployeeRoleType } from 'types/matters';
import { getClassifierText, t } from 'utils/getTranslations';
import { secondsToHours } from 'utils/time';

const formatBoolean = (value?: boolean): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }
    return value ? t('yes') : t('no');
};

export const mapMatterExperienceRecordsToCSVFormat = (
    items: MatterExperienceRecordListExportItem[],
    classifiers: Record<string, ClassifierData>,
    options?: ExportMapperOptions
) => {
    return items.map((item) => {
        const matter = item.matter;

        return {
            // 1. Client name (from connected Case)
            [t('experienceRecordExport.client-name')]: matter?.clientParty?.name,
            // 2. Connected cases (number + name)
            [t('experienceRecordExport.connected-cases')]: matter
                ? [matter.caseNumber, matter.name].filter(Boolean).join(' ')
                : undefined,
            // 3. Case description
            [t('experienceRecordExport.case-description')]: matter?.description,
            // 4. Case status
            [t('experienceRecordExport.case-status')]: getClassifierText(
                classifiers['MATTER_STATE_TYPE'],
                matter?.matterStateTypeCode
            ),
            // 5. Supervising partner
            [t('experienceRecordExport.supervising-partner')]: matter?.supervisedByEmployee?.name,
            // 6. Responsible lawyer
            [t('experienceRecordExport.responsible-lawyer')]: matter?.responsibleEmployee?.name,
            // 7. Handled by employees
            [t('experienceRecordExport.handled-by-employees')]: (matter?.employees ?? [])
                .filter((e) => e.matterEmployeeRoleTypeCode === MatterEmployeeRoleType.STAFF)
                .map((e) => e.employee.name)
                .join(', '),
            // 8. Handled by office
            [t('experienceRecordExport.handled-by-office')]: matter?.handledByOffice?.name,
            // 9. Case opening date
            [t('experienceRecordExport.case-opening-date')]: toExcelDate(matter?.openedDate),
            // 10. Case closing date
            [t('experienceRecordExport.case-closing-date')]: toExcelDate(matter?.closedDate),
            // 11. Total hours
            [t('experienceRecordExport.total-hours')]: toExcelQuantity(
                matter?.totalBillableTimeInSeconds != null ? secondsToHours(matter.totalBillableTimeInSeconds, 2) : null
            ),
            // 12. Total cash
            [t('experienceRecordExport.total-cash')]: toExcelAmount(matter?.totalBillableAmountInOriginalCurrency),
            // 13. Case is confidential
            [t('experienceRecordExport.case-is-confidential')]: formatBoolean(matter?.isConfidential),

            // 14. Client name for XR description
            [t('experienceRecordExport.xr-client-name')]: item.xrClientName,
            // 15. XR name
            [t('experienceRecordExport.xr-name')]: item.xrName,
            // 16. Description in English
            [t('experienceRecordExport.description-en')]: item.descriptionEn,
            // 17. Description in local language
            [t('experienceRecordExport.description-local-lang')]: item.descriptionLocalLang,
            // 18. XR date
            [t('experienceRecordExport.xr-date')]: toExcelDate(item.xrDate),
            // 19. XR is confidential
            [t('experienceRecordExport.xr-is-confidential')]: formatBoolean(item.xrIsConfidential),
            // 20. Connected business areas (from matter client party)
            [t('experienceRecordExport.connected-business-areas')]: (matter?.partyBusinessAreas ?? [])
                .map(({ businessAreaCode }) => getClassifierText(classifiers['BUSINESS_AREA_TYPE'], businessAreaCode))
                .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                .join(', '),
            // 21. Connected sectors (sector-group teams serving the client party)
            [t('experienceRecordExport.connected-sectors')]: (matter?.servedBySectorGroups ?? [])
                .map(({ name }) => name)
                .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                .join(', '),
            // 22. Connected jurisdiction
            [t('experienceRecordExport.connected-jurisdiction')]: (item.jurisdictions ?? [])
                .map((code) => getClassifierText(classifiers['COUNTRY'], code))
                .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                .join(', '),
            // 23. Deal/Dispute value
            [t('experienceRecordExport.deal-value')]: item.dealValueAmount,
            // 24. Value currency
            [t('experienceRecordExport.value-currency')]: item.valueCurrencyCode,
            // 25. Value is confidential
            [t('experienceRecordExport.value-is-confidential')]: formatBoolean(item.isValueConfidential),
            // 26. Client consent requested date
            [t('experienceRecordExport.consent-requested-on')]: toExcelDate(item.clientConsentRequestedOn),
            // 27. Client consent received date
            [t('experienceRecordExport.consent-received-on')]: toExcelDate(item.clientConsentReceivedOn),
            // 28. Showcase priority
            [t('experienceRecordExport.showcase-priority')]: formatBoolean(item.isShowcasePriority),
            // 29. Used in marketing
            [t('experienceRecordExport.used-in-marketing')]: formatBoolean(item.isUsedInMarketing),
            // 30. Included in legal directories
            [t('experienceRecordExport.included-in-legal-directories')]: formatBoolean(
                item.isIncludedInLegalDirectories
            ),
            // 31. Referee name, named for the candidates they are once Rankings referees is on
            [t(
                options?.isRankingsRefereeEnabled
                    ? 'experienceRecordExport.referee-candidates'
                    : 'experienceRecordExport.referee-name'
            )]: (item.referees ?? []).map(({ name }) => name).join(', '),
        };
    });
};
